// Data única no carrinho (05/10/2026, pedido do Pedro — caso Koma): o cliente escolhe UMA data e UM horário
// para o pedido inteiro. Esta é a conta do calendário do CARRINHO, a mesma no link (checkout) e no PDV. O
// servidor não muda a regra: ao receber o pedido confere item a item (prazo, dias do produto, capacidade e
// "uma encomenda por horário" ou "por dia"), com a mesma data em todos.
//
// Encomenda por turno ou só pela data (12/10/2026): com produtos de modos diferentes vale o MAIS DETALHADO
// (decisão do Pedro) — algum pede horário, o cliente escolhe o horário; senão, algum pede turno, escolhe o turno;
// senão, só a data. Produto por turno num carrinho de horário limita os horários aos turnos dele; produto de só a
// data não limita horário nem turno (só os dias).
import type { ProductAvailabilityConfig } from "@workspace/api-client-react";
import {
  HORARIO_DOS_TURNOS_PADRAO,
  inicioDoDia,
  modoDaEncomenda,
  modoMaisDetalhado,
  turnoDoHorario,
  turnosDoProduto,
  turnosLigados,
  type HorarioDosTurnos,
  type ModoEncomenda,
  type Turno,
} from "@workspace/pricing";
import {
  getAvailableTimeSlotsForDate,
  getWindowsForDate,
  isAvailabilityConfigActive,
  isDateAllowed,
  primeiroMomentoEncomenda,
  summarizeAvailabilityConfig,
  turnosDoDia,
} from "@/lib/availability";
import { getStoreTimezone } from "@/lib/store-timezone";

export type ItemDeEncomenda = {
  productId: string;
  nome: string;
  quantidade: number;
  availabilityConfig: ProductAvailabilityConfig | null | undefined;
  minAdvanceDays: number;
  minAdvanceHours: number | null;
  modo: ModoEncomenda;
  turnos: Turno[];
  // «Aceitar encomendas» de/até (26/10/2026): o primeiro e o último dia aceitos, "AAAA-MM-DD"; null = sem limite.
  de: string | null;
  ate: string | null;
};

export type CapacidadeDoCarrinho = {
  produtos: Record<
    string,
    {
      // Os mesmos limites, do servidor (valem mais que os do produto guardado no carrinho).
      orderFromDate?: string | null;
      orderUntilDate?: string | null;
      dailyCapacity: number | null;
      bookedCounts: Record<string, number>;
      orderShiftCapacity?: number | null;
      shiftBookedCounts?: Record<string, Record<string, number>>;
    }
  >;
  slotExclusive: boolean;
  slotIntervalMinutes: number;
  bookedTimes: Record<string, string[]>;
  bookedShifts?: Record<string, string[]>;
  // Até N pedidos por dia (18/10/2026; antes «uma por dia»): `bookedDays` = pedidos com encomenda em cada dia e
  // `dayLimit` = o N (null = sem limite). O `dayExclusive` continua vindo (= há limite); servidor antigo só manda ele (N = 1).
  dayExclusive?: boolean;
  dayLimit?: number | null;
  bookedDays?: Record<string, number>;
};

type CapacidadeDoDia = Pick<CapacidadeDoCarrinho, "dayExclusive" | "dayLimit" | "bookedDays">;

// Quantos pedidos com encomenda o dia aceita (null = sem limite).
export function limiteDoDia(cap: CapacidadeDoDia | null | undefined): number | null {
  return cap?.dayLimit ?? (cap?.dayExclusive ? 1 : null);
}

// O dia já está no limite com pedidos de OUTROS clientes. `diasDoPedido`: os dias do pedido que o PDV está editando
// (ele não conta contra si).
export function diaOcupado(cap: CapacidadeDoDia | null | undefined, ds: string, diasDoPedido: string[] = []): boolean {
  const limite = limiteDoDia(cap);
  if (!limite) return false;
  return (cap?.bookedDays?.[ds] ?? 0) - (diasDoPedido.includes(ds) ? 1 : 0) >= limite;
}

export type Prazo = { data: string; hora: string | null; quem: string };

// Itens por encomenda do carrinho, um por produto (linhas do mesmo produto somam a quantidade).
export function itensDeEncomenda(
  linhas: Array<{ product: any; quantity: number }>,
): ItemDeEncomenda[] {
  const porProduto = new Map<string, ItemDeEncomenda>();
  for (const l of linhas) {
    const p = l.product;
    if (!p?.isOrderBased) continue;
    const atual = porProduto.get(p.id);
    if (atual) {
      atual.quantidade += l.quantity;
      continue;
    }
    porProduto.set(p.id, {
      productId: p.id,
      nome: p.name,
      quantidade: l.quantity,
      availabilityConfig: p.availabilityConfig ?? null,
      minAdvanceDays: p.minAdvanceDays ?? 0,
      minAdvanceHours: p.minAdvanceHours ?? null,
      modo: modoDaEncomenda(p),
      turnos: turnosDoProduto(p),
      de: typeof p.orderFromDate === "string" && p.orderFromDate ? p.orderFromDate.slice(0, 10) : null,
      ate: typeof p.orderUntilDate === "string" && p.orderUntilDate ? p.orderUntilDate.slice(0, 10) : null,
    });
  }
  return [...porProduto.values()];
}

// O primeiro e o último dia aceitos pelo item: os do servidor quando a capacidade já chegou, senão os do produto.
export function inicioDoItem(it: ItemDeEncomenda, cap: Pick<CapacidadeDoCarrinho, "produtos"> | null | undefined): string | null {
  const c = cap?.produtos?.[it.productId];
  if (c && "orderFromDate" in c) return c.orderFromDate ? c.orderFromDate.slice(0, 10) : null;
  return it.de;
}
export function limiteDoItem(it: ItemDeEncomenda, cap: Pick<CapacidadeDoCarrinho, "produtos"> | null | undefined): string | null {
  const c = cap?.produtos?.[it.productId];
  if (c && "orderUntilDate" in c) return c.orderUntilDate ? c.orderUntilDate.slice(0, 10) : null;
  return it.ate;
}

// Na data única vale o fim mais cedo entre os itens (e quem o põe).
export function limiteDoCarrinho(
  itens: ItemDeEncomenda[],
  cap: Pick<CapacidadeDoCarrinho, "produtos"> | null | undefined,
): { data: string; quem: string } | null {
  let menor: { data: string; quem: string } | null = null;
  for (const it of itens) {
    const ate = limiteDoItem(it, cap);
    if (ate && (!menor || ate < menor.data)) menor = { data: ate, quem: it.nome };
  }
  return menor;
}

// E o início mais tarde.
export function inicioDoCarrinho(
  itens: ItemDeEncomenda[],
  cap: Pick<CapacidadeDoCarrinho, "produtos"> | null | undefined,
): { data: string; quem: string } | null {
  let maior: { data: string; quem: string } | null = null;
  for (const it of itens) {
    const de = inicioDoItem(it, cap);
    if (de && (!maior || de > maior.data)) maior = { data: de, quem: it.nome };
  }
  return maior;
}

// A frase da janela de um produto: «de 01/12 a 20/12», «a partir de 01/12» ou «até 10/11» (null = sem janela).
export function janelaDaEncomenda(de: string | null, ate: string | null): string | null {
  if (de && ate) return `de ${dataCurta(de)} a ${dataCurta(ate)}`;
  if (de) return `a partir de ${dataCurta(de)}`;
  if (ate) return `até ${dataCurta(ate)}`;
  return null;
}

export function modoDoCarrinho(itens: ItemDeEncomenda[]): ModoEncomenda {
  return modoMaisDetalhado(itens.map((i) => i.modo));
}

export function hojeNaLoja(agora: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: getStoreTimezone(),
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(agora);
}

export function somaDias(ds: string, n: number): string {
  const [y, m, d] = ds.split("-").map(Number);
  return new Date(Date.UTC(y!, m! - 1, d! + n)).toISOString().slice(0, 10);
}

// Grade padrão do link para produto sem disponibilidade configurada (a mesma do OrderSchedulePicker).
export function gradePadrao(passo: number): string[] {
  const step = passo > 0 ? passo : 30;
  const out: string[] = [];
  for (let t = 8 * 60; t <= 20 * 60; t += step) {
    out.push(`${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`);
  }
  return out;
}

// Horários do item no dia; null = o item não limita o horário (só a data).
function horariosDoItem(it: ItemDeEncomenda, ds: string, passo: number, horarios: HorarioDosTurnos): string[] | null {
  if (it.modo === "data") return null;
  const grade = isAvailabilityConfigActive(it.availabilityConfig)
    ? getAvailableTimeSlotsForDate(it.availabilityConfig, ds, passo)
    : gradePadrao(passo);
  return it.modo === "turno" ? grade.filter((t) => turnoDoHorario(t, horarios, it.turnos) !== null) : grade;
}

// O prazo do carrinho é o MAIS LONGO entre os itens: em dias, o começo do dia (hoje + N) no relógio da loja; em
// horas, agora + H. `hora` null = qualquer horário daquele dia.
export function prazoDoCarrinho(itens: ItemDeEncomenda[], agora: Date = new Date()): Prazo | null {
  let melhor: Prazo | null = null;
  const hoje = hojeNaLoja(agora);
  for (const it of itens) {
    let p: { data: string; hora: string | null };
    if (it.minAdvanceHours) p = primeiroMomentoEncomenda(it.minAdvanceHours, agora);
    else p = { data: somaDias(hoje, Math.max(0, it.minAdvanceDays)), hora: null };
    const k = `${p.data}T${p.hora ?? "00:00"}`;
    if (!melhor || k > `${melhor.data}T${melhor.hora ?? "00:00"}`) melhor = { ...p, quem: it.nome };
  }
  return melhor;
}

// Horários que servem a TODOS os itens no dia: interseção das grades, sem os de antes do prazo e (com "uma
// encomenda por horário") sem os já reservados por outro pedido.
export function horariosDoCarrinho(
  itens: ItemDeEncomenda[],
  ds: string,
  cap: Pick<CapacidadeDoCarrinho, "slotExclusive" | "slotIntervalMinutes" | "bookedTimes"> | null,
  prazo: Prazo | null,
  opcoes: { semPrazo?: boolean; comOcupados?: boolean } = {},
  horarios: HorarioDosTurnos = HORARIO_DOS_TURNOS_PADRAO,
): string[] {
  if (itens.length === 0) return [];
  const passo = cap?.slotIntervalMinutes ?? 30;
  let comuns: string[] | null = null;
  for (const it of itens) {
    const deste = horariosDoItem(it, ds, passo, horarios);
    if (deste === null) continue;
    if (comuns === null) comuns = deste;
    else {
      const set = new Set(deste);
      comuns = comuns.filter((t) => set.has(t));
    }
  }
  let lista: string[] = comuns ?? [];
  if (!opcoes.semPrazo && prazo && ds === prazo.data && prazo.hora) lista = lista.filter((t) => t >= prazo.hora!);
  if (!opcoes.comOcupados && cap?.slotExclusive) {
    const ocupados = new Set(cap.bookedTimes[ds] ?? []);
    lista = lista.filter((t) => !ocupados.has(t));
  }
  return lista;
}

// Turnos que servem a TODOS os itens por turno no dia (os de só a data não limitam): ligados em cada um, cobertos
// pela disponibilidade do dia, começando depois do prazo; sem o reservado (uma por turno) e sem o que estoura o
// limite por turno de algum produto.
export function turnosDoCarrinho(
  itens: ItemDeEncomenda[],
  ds: string,
  cap: Pick<CapacidadeDoCarrinho, "slotExclusive" | "bookedShifts" | "produtos"> | null,
  prazo: Prazo | null,
  horarios: HorarioDosTurnos = HORARIO_DOS_TURNOS_PADRAO,
  opcoes: { semPrazo?: boolean; comOcupados?: boolean } = {},
): Turno[] {
  const primeiro = !opcoes.semPrazo && prazo?.hora ? { data: prazo.data, hora: prazo.hora } : null;
  let comuns: Turno[] = turnosLigados(horarios);
  for (const it of itens) {
    if (it.modo !== "turno") continue;
    const deste = new Set(turnosDoDia(it.availabilityConfig, ds, { modo: "turno", turnos: it.turnos, horarios }, primeiro));
    comuns = comuns.filter((t) => deste.has(t));
  }
  if (!opcoes.comOcupados) comuns = comuns.filter((t) => !turnoIndisponivel(itens, ds, t, cap));
  return comuns;
}

// Turno reservado por outro pedido (uma por turno) ou cheio para algum produto (limite por turno).
export function turnoIndisponivel(
  itens: ItemDeEncomenda[],
  ds: string,
  t: Turno,
  cap: Pick<CapacidadeDoCarrinho, "slotExclusive" | "bookedShifts" | "produtos"> | null,
): string | null {
  if (!cap) return null;
  if (cap.slotExclusive && (cap.bookedShifts?.[ds] ?? []).includes(t)) return "já tem encomenda nesse turno e a loja está em uma por horário";
  for (const it of itens) {
    const c = cap.produtos[it.productId];
    if (c?.orderShiftCapacity && (c.shiftBookedCounts?.[ds]?.[t] ?? 0) + it.quantidade > c.orderShiftCapacity) {
      return `${it.nome}: sem vaga nesse turno`;
    }
  }
  return null;
}

// Por que o dia não serve (null = serve).
export function motivoDoDia(
  itens: ItemDeEncomenda[],
  ds: string,
  cap: CapacidadeDoCarrinho | null,
  prazo: Prazo | null,
  horarios: HorarioDosTurnos = HORARIO_DOS_TURNOS_PADRAO,
  diasDoPedido: string[] = [],
): string | null {
  if (prazo && ds < prazo.data) return "antes do prazo";
  for (const it of itens) {
    const de = inicioDoItem(it, cap);
    if (de && ds < de) return `${it.nome} aceita encomendas a partir de ${dataCurta(de)}`;
    const ate = limiteDoItem(it, cap);
    if (ate && ds > ate) return `${it.nome} aceita encomendas até ${dataCurta(ate)}`;
  }
  for (const it of itens) {
    if (isAvailabilityConfigActive(it.availabilityConfig) && !isDateAllowed(it.availabilityConfig, ds)) {
      return `${it.nome} não é feito neste dia`;
    }
  }
  if (cap) {
    for (const it of itens) {
      const c = cap.produtos[it.productId];
      if (c?.dailyCapacity && (c.bookedCounts[ds] ?? 0) + it.quantidade > c.dailyCapacity) {
        return `${it.nome}: sem vaga neste dia`;
      }
    }
    if (diaOcupado(cap, ds, diasDoPedido)) {
      return limiteDoDia(cap) === 1 ? "já tem encomenda neste dia e a loja está em uma por dia" : "as encomendas deste dia já estão completas";
    }
  }
  const modo = modoDoCarrinho(itens);
  if (modo === "turno") {
    if (turnosDoCarrinho(itens, ds, cap, prazo, horarios).length === 0) return "sem turno em comum";
    return null;
  }
  if (modo === "data") {
    // Prazo em horas no "só a data": o dia conta se começa depois do prazo (para cada produto).
    if (prazo?.hora && ds === prazo.data) {
      for (const it of itens) {
        const faixas = isAvailabilityConfigActive(it.availabilityConfig) ? getWindowsForDate(it.availabilityConfig, ds) : null;
        if (`${ds}T${inicioDoDia(faixas)}` < `${prazo.data}T${prazo.hora}`) return "antes do prazo";
      }
    }
    return null;
  }
  if (horariosDoCarrinho(itens, ds, cap, prazo, {}, horarios).length === 0) return "sem horário em comum";
  return null;
}

// Itens que nunca se encontram: nenhum dia nos próximos `horizonte` dias em que todos sejam feitos e tenham
// horário (ou turno) em comum (só pela disponibilidade de cada produto; capacidade muda com o tempo e não entra aqui).
export function conflitoDeDias(
  itens: ItemDeEncomenda[],
  agora: Date = new Date(),
  horizonte = 60,
  horarios: HorarioDosTurnos = HORARIO_DOS_TURNOS_PADRAO,
): string | null {
  // «Aceitar encomendas» de/até (26/10/2026): o início mais tarde depois do fim mais cedo = nenhum dia em comum.
  const inicio = inicioDoCarrinho(itens, null), fim = limiteDoCarrinho(itens, null);
  if (inicio && fim && inicio.data > fim.data) {
    return itens.filter((i) => i.de || i.ate).map((i) => `${i.nome}: ${janelaDaEncomenda(i.de, i.ate)}`).join(" · ");
  }
  const restritos = itens.filter((i) => isAvailabilityConfigActive(i.availabilityConfig));
  if (restritos.length < 2) return null;
  const hoje = hojeNaLoja(agora);
  const modo = modoDoCarrinho(itens);
  for (let i = 0; i <= horizonte; i++) {
    const ds = somaDias(hoje, i);
    if (restritos.some((it) => !isDateAllowed(it.availabilityConfig, ds))) continue;
    if (modo === "data") return null;
    if (modo === "turno" && turnosDoCarrinho(itens, ds, null, null, horarios, { semPrazo: true, comOcupados: true }).length > 0) return null;
    if (modo === "horario" && horariosDoCarrinho(itens, ds, null, null, { semPrazo: true, comOcupados: true }, horarios).length > 0) return null;
  }
  return restritos.map((i) => `${i.nome}: ${summarizeAvailabilityConfig(i.availabilityConfig) || "dias específicos"}`).join(" · ");
}

export function dataCurta(ds: string): string {
  const [, m, d] = ds.split("-");
  return `${d}/${m}`;
}

// Data, hora e turno em comum dos itens agendados de um pedido (para mostrar UMA vez). null = sem item agendado
// ou itens com datas/horários/turnos diferentes (pedido antigo, PDV com data por item).
export function dataUnicaDosItens(
  itens: Array<{ scheduledDate?: string | null; scheduledTime?: string | null; scheduledShift?: string | null }>,
): { data: string; hora: string | null; turno: string | null } | null {
  const agendados = itens.filter((i) => i.scheduledDate);
  if (agendados.length === 0) return null;
  const a = agendados[0]!;
  const igual = agendados.every(
    (i) =>
      i.scheduledDate === a.scheduledDate &&
      (i.scheduledTime ?? null) === (a.scheduledTime ?? null) &&
      (i.scheduledTime ? true : (i.scheduledShift ?? null) === (a.scheduledShift ?? null)),
  );
  return igual ? { data: a.scheduledDate!, hora: a.scheduledTime ?? null, turno: a.scheduledTime ? null : a.scheduledShift ?? null } : null;
}
